'use client';

/**
 * Sonner toaster bound to next-themes so site ThemeToggle drives toast chrome.
 * Follows Emil Kowalski’s Sonner model (stack / swipe / hover-expand).
 *
 * Every toast has a × (`closeButton`); hovering pauses the auto-close timer.
 * Default `duration` is only the fallback — `@/lib/toast` sets durations by
 * severity (success 4s, warning 6s, error 10s, anything with an action stays).
 * This is the one place besides `lib/toast.ts` allowed to import `sonner`.
 *
 * Two Toasters: the main bottom-right stack for everything, and a top-center
 * one that holds only the version / session / idle guard notices
 * (`toasterId: GUARD_TOASTER_ID`). The main stack caps how many toasts it
 * shows, and persistent job toasts could otherwise push a guard notice — the
 * one that signs you out — out of view.
 */
import { c as _c } from "react/compiler-runtime";
import { useTheme } from 'next-themes';
import { useSyncExternalStore } from 'react';
import { Toaster } from 'sonner';
import { GUARD_TOASTER_ID, GUARD_TOASTER_VISIBLE } from '@/lib/guard-toasts';

/**
 * Room in the main stack for all four Compli job toasts at once (bulk run,
 * Docs Posture bulk, Doc Assessment, Docs Posture), which stay until handled.
 */
const MAIN_VISIBLE_TOASTS = 4;
export function CrucibleToaster() {
  const $ = _c(5);
  const {
    resolvedTheme
  } = useTheme();
  const mounted = useSyncExternalStore(_temp2, _temp3, _temp4);
  const theme = !mounted || resolvedTheme !== "light" ? "dark" : "light";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      className: "crucible-toast",
      closeButtonAriaLabel: "Dismiss notification"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== theme) {
    t1 = {
      theme,
      closeButton: true,
      richColors: false,
      duration: 4000,
      gap: 14,
      offset: 24,
      className: "crucible-toaster",
      toastOptions: t0
    };
    $[1] = theme;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shared = t1 as const;
  let t2;
  if ($[3] !== shared) {
    t2 = <><Toaster {...shared} position="bottom-right" expand={false} visibleToasts={MAIN_VISIBLE_TOASTS} /><Toaster {...shared} id={GUARD_TOASTER_ID} position="top-center" expand={true} visibleToasts={GUARD_TOASTER_VISIBLE} /></>;
    $[3] = shared;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function _temp4() {
  return false;
}
function _temp3() {
  return true;
}
function _temp2() {
  return _temp;
}
function _temp() {}